/**
 * v3.15-1154 — FAQ page (public, no auth).
 *
 * Accessible disclosure pattern using native <details>/<summary>.
 */
import PublicLayout from '../components/PublicLayout';
import EndPageCTA from '../components/EndPageCTA';
import { ChevronDown } from 'lucide-react';

const FAQS = [
  {
    q: 'How does Prestigious Hub work?',
    a: `Prestigious Hub is the platform partners use to submit clients into our consumer
        funding, business funding, and credit improvement programs. Every submission is
        routed to a dedicated Application Concierge who personally guides the file from
        application through funding. Partners maintain real-time visibility into the
        deal at every stage.`,
  },
  {
    q: 'How do I track my clients?',
    a: `Partners log in to their dashboard and see the live status of every client they have
        submitted — current stage, documents pending, lender activity, approval state, and
        funding timeline. Updates appear as soon as our Concierge or system records them.`,
  },
  {
    q: 'Will my client receive a dedicated Application Concierge?',
    a: `Yes. Every client we receive is assigned a single Application Concierge who is
        responsible for that file end-to-end. The Concierge handles documentation,
        client communication, lender coordination, and funding logistics — so the
        client and the partner always have one human point of contact.`,
  },
  {
    q: 'How many lending partners do you work with?',
    a: `Prestigious Hub works with a growing nationwide network of nearly 150 lending
        partners, covering consumer financing, business financing, and specialty
        commercial capital programs.`,
  },
  {
    q: "What happens if my client isn't approved?",
    a: `If a client isn't approved through their initial path, our Concierge explores
        alternative options before the file is considered complete. When appropriate,
        clients are referred to CreditForward360 so we can rebuild their credit profile
        and try again at a later stage.`,
  },
  {
    q: 'Do financing options require a hard inquiry?',
    a: `Many of our financing options begin with a soft inquiry pre-approval, allowing
        clients to see realistic options without affecting their credit score. Hard
        inquiries are only performed when a client formally moves forward with a
        specific lender.`,
  },
  {
    q: 'How does CreditForward360 work?',
    a: `CreditForward360 is our credit improvement program. When a client needs a
        stronger financial profile before they can be funded — or after a partial
        approval — the Concierge routes them into CreditForward360 to rebuild their
        credit. Once the profile improves, clients re-enter the funding workflow.`,
  },
  {
    q: 'How does Business Funding work?',
    a: `Through Prestigious Hub partners can submit clients for a range of commercial
        and business funding programs including working capital, equipment financing,
        expansion capital, and other tailored commercial lending products — all processed
        with the same Concierge experience as our consumer funding programs.`,
  },
  {
    q: 'How does ACH processing work?',
    a: `Prestigious Hub processes payments securely via ACH transfers. Partners and
        clients are paid through standardized ACH flows with full audit trails
        recorded in the Hub.`,
  },
  {
    q: 'How does same-day funding work?',
    a: `Where available, qualified files can be funded the same day they are approved
        using same-day wire capability. Your Application Concierge confirms eligibility,
        coordinates the wire with the lender, and keeps the partner informed in real
        time until funds land.`,
  },
];

const FAQItem = ({ q, a, idx }) => (
  <details
    className="group border-b border-slate-200 py-4"
    data-testid={`faq-item-${idx}`}
  >
    <summary className="flex items-center justify-between cursor-pointer list-none">
      <h3 className="font-heading text-lg font-semibold text-slate-800 pr-6">{q}</h3>
      <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 transition-transform group-open:rotate-180" />
    </summary>
    <p className="text-slate-600 leading-relaxed mt-3 pr-8">{a}</p>
  </details>
);

const FaqPage = () => (
  <PublicLayout testId="faq-page">
    <section className="bg-white py-24 px-6">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Frequently Asked</p>
        <h1 className="font-heading text-5xl font-bold text-primary mb-10 leading-tight">
          Questions partners ask before they sign up.
        </h1>
        <div>
          {FAQS.map((item, i) => <FAQItem key={item.q} {...item} idx={i} />)}
        </div>
      </div>
    </section>
    <EndPageCTA testId="faq-end-cta" />
  </PublicLayout>
);

export default FaqPage;
